import type { Tenant } from '@shared/types/Tenant';

/** Champs légaux requis pour générer bulletins / attestations / déclarations. */
export interface CompanyLegalRequired {
  rccm: string;
  nif: string;
  legal_form: string;
  registered_name: string;
  headquarters: string;
}

export function companyLegalFromTenant(tenant: Tenant | null | undefined): {
  rccm: string;
  nif: string;
  legal_form: string;
  registered_name: string;
  trade_name: string;
  headquarters: string;
  capital: number;
  phone: string;
  email: string;
  website: string;
  cnss_employer_id: string;
  cnamgs_id: string;
} {
  const p = tenant?.parametres;
  return {
    rccm: (tenant?.rccm ?? '').trim(),
    nif: (tenant?.nif ?? '').trim(),
    legal_form: (tenant?.forme_juridique ?? '').trim(),
    registered_name: (tenant?.raison_sociale ?? tenant?.nom ?? '').trim(),
    trade_name: (p?.trade_name ?? '').trim(),
    headquarters: [tenant?.adresse, tenant?.ville].filter(Boolean).join(', ').trim(),
    capital: tenant?.capital_social ?? 0,
    phone: (tenant?.telephone ?? '').trim(),
    email: (tenant?.email ?? '').trim(),
    website: (p?.website ?? '').trim(),
    cnss_employer_id: (p?.numero_cnss_employeur ?? '').trim(),
    cnamgs_id: (p?.numero_cnamgs_employeur ?? '').trim(),
  };
}

/** True si RCCM + NIF + forme + dénomination + siège sont renseignés. */
export function isCompanyLegalComplete(tenant: Tenant | null | undefined): boolean {
  const info = companyLegalFromTenant(tenant);
  return Boolean(
    info.rccm && info.nif && info.legal_form && info.registered_name && info.headquarters,
  );
}

/** Mentions employeur pour bulletins DGI (RCCM, NIF, CNSS) — warning si incomplet. */
export function companyLegalBulletinWarnings(tenant: Tenant | null | undefined): string[] {
  const info = companyLegalFromTenant(tenant);
  const missing: string[] = [];
  if (!info.rccm) missing.push('RCCM');
  if (!info.nif) missing.push('NIF');
  if (!info.cnss_employer_id) missing.push('N° CNSS employeur');
  return missing;
}

export const COMPANY_LEGAL_WARNING =
  'Complétez les informations légales dans SYSTÈME → Paramètres → Identité légale avant de générer ce document.';

/** Mini en-tête document pour prévisualisation Paramètres. */
export function companyLegalPreviewHtml(tenant: Tenant | null | undefined): string {
  const info = companyLegalFromTenant(tenant);
  const name = info.trade_name || info.registered_name || '—';
  return `<div style="border:1px solid var(--border,#C7D0D6);border-radius:8px;padding:12px 14px;background:#f8fafb;font-size:13px;line-height:1.55;color:#0a121e">
    <strong style="font-size:15px">${escapeHtml(name)}</strong>
    ${info.legal_form ? ` · ${escapeHtml(info.legal_form)}` : ''}
    <div style="color:#586069;margin-top:4px">
      ${info.rccm ? `RCCM ${escapeHtml(info.rccm)}` : '<span style="color:#b45309">RCCM manquant</span>'}
      · ${info.nif ? `NIF ${escapeHtml(info.nif)}` : '<span style="color:#b45309">NIF manquant</span>'}
    </div>
    <div style="color:#586069">${info.headquarters ? escapeHtml(info.headquarters) : '<span style="color:#b45309">Siège social manquant</span>'}</div>
  </div>`;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
